
import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useOutletContext } from 'react-router-dom';
import pb from '../../lib/pocketbase';
import styles from '../Dashboard.module.css';
import { FaPaperPlane, FaSearch, FaUser, FaReply, FaCheckDouble, FaCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';

const Messages = () => {
  // context data (if available) - though we might prefer direct fetching for full control
  const { messages: initialMessages } = useOutletContext();

  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [replyContent, setReplyContent] = useState('');
  const [loadingChat, setLoadingChat] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);

  // Ref for selectedUser to use in realtime callback without stale closures
  const selectedUserRef = useRef(selectedUser);
  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  // Auto-scroll ref
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    let isMounted = true;

    fetchUsersAndConversations(isMounted);

    // Real-time for NEW messages
    const initRealtime = async () => {
      try {
        if (!pb.authStore.isValid) return;

        // Ensure we are unsubscribed first to prevent double subscriptions if React double-mounts
        try {
          await pb.collection('messages').unsubscribe('*');
        } catch (e) {
          // Ignore close errors
        }

        await pb.collection('messages').subscribe('*', function (e) {
          if (!isMounted) return;

          if (e.action === 'create') {
            const currentSelectedUser = selectedUserRef.current;
            // If it's for current thread, append
            if (currentSelectedUser && (e.record.sender === currentSelectedUser.id || e.record.recipient === currentSelectedUser.id)) {
              setChatHistory(prev => {
                if (prev.some(m => m.id === e.record.id)) return prev;
                return [...prev, e.record];
              });
              setTimeout(() => {
                if (isMounted) scrollToBottom();
              }, 100);
            }
            // Refresh list to show unread dot
            fetchUsersAndConversations(isMounted);
          }
        }).catch(err => {
          if (err.status !== 0) console.error(err);
        });
      } catch (err) {
        console.error("Realtime subscription error in Messages:", err);
      }
    };

    initRealtime();

    return () => {
      isMounted = false;
      pb.collection('messages').unsubscribe('*').catch(err => {
        console.warn("Error unsubscribing from messages:", err);
      });
    };
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchChatHistory(selectedUser.id);
    }
  }, [selectedUser]);


  const fetchUsersAndConversations = async (isMounted = true) => {
    try {
      // Logic: Get all users who have sent messages + all users who are clients
      const allUsers = await pb.collection('users').getFullList({
        sort: 'name',
      });

      // Fetch all messages to group by user
      const allMessages = await pb.collection('messages').getFullList({
        sort: '-created',
        expand: 'sender'
      });

      if (!isMounted) return;

      // Process to find last message per user & unread count
      const userMap = allUsers.map(user => {
        const userMsgs = allMessages.filter(m => m.sender === user.id || m.recipient === user.id);
        const lastMsg = userMsgs.length > 0 ? userMsgs[0] : null;
        const unread = userMsgs.filter(m => m.sender === user.id && !m.is_read).length;

        return {
          ...user,
          lastMessage: lastMsg,
          unreadCount: unread,
          hasHistory: userMsgs.length > 0
        };
      });

      // Sorting: Unread first, then by last message date
      const sortedUsers = userMap
        .filter(u => u.hasHistory || u.role === 'client')
        .sort((a, b) => {
          if (b.unreadCount !== a.unreadCount) return b.unreadCount - a.unreadCount;
          const dateA = a.lastMessage ? new Date(a.lastMessage.created) : new Date(0);
          const dateB = b.lastMessage ? new Date(b.lastMessage.created) : new Date(0);
          return dateB - dateA;
        });

      setUsers(sortedUsers);
    } catch (err) {
      console.error("Error fetching conversations:", err);
      if (isMounted) toast.error("Erreur chargement messagerie");
    }
  };

  const fetchChatHistory = async (userId) => {
    if (!pb.authStore.model) return;
    setLoadingChat(true);
    try {
      // Fetch both sent and received
      const history = await pb.collection('messages').getList(1, 100, {
        filter: `(sender="${userId}" && recipient="${pb.authStore.model.id}") || (sender="${pb.authStore.model.id}" && recipient="${userId}")`,
        sort: 'created', // Chronological order for chat view
        expand: 'sender'
      });

      setChatHistory(history.items);
      scrollToBottom();

      // Mark visible unread messages as read
      const unreadIds = history.items
        .filter(m => m.sender === userId && !m.is_read)
        .map(m => m.id);

      if (unreadIds.length > 0) {
        await Promise.all(unreadIds.map(id => pb.collection('messages').update(id, { is_read: true })));
        // Update local unread count UI immediately
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, unreadCount: 0 } : u));
        // Also update global context if function provided (handleViewMessage usually does this singly)
      }

    } catch (err) {
      console.error("Error fetching chat:", err);
    } finally {
      setLoadingChat(false);
    }
  };

  const handleSendMessage = async () => {
    if (!replyContent.trim() || !selectedUser) return;

    try {
      const newMsg = await pb.collection('messages').create({
        sender: pb.authStore.model.id,
        recipient: selectedUser.id,
        body: replyContent, // Plain text or HTML from Quill? Quill returns HTML usually.
        subject: "Réponse Support", // Generic subject for chat mode
        is_read: false
      });

      setChatHistory([...chatHistory, newMsg]);
      setReplyContent('');
      scrollToBottom();

      // Update user list last message preview
      setUsers(prev => prev.map(u =>
        u.id === selectedUser.id
          ? { ...u, lastMessage: newMsg }
          : u
      ));

    } catch (err) {
      toast.error("Erreur d'envoi");
    }
  };

  const getInitials = (name) => {
    return name ? name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'USER';
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) 2fr', gap: '20px', height: 'calc(100vh - 150px)', minHeight: '600px' }}>

      {/* LEFT: User List */}
      <div className={styles.card} style={{ display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden' }}>
        <div style={{ padding: '20px', borderBottom: '1px solid var(--color-light-gray, #eee)' }}>
          <h3 style={{ margin: '0 0 15px 0' }}>Discussions</h3>
          <div style={{ position: 'relative' }}>
            <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#aaa' }} />
            <input
              type="text"
              placeholder="Rechercher un client..."
              className={styles.searchInput}
              style={{ width: '100%', paddingLeft: '35px' }}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filteredUsers.map(user => (
            <div
              key={user.id}
              onClick={() => setSelectedUser(user)}
              style={{
                padding: '15px 20px',
                borderBottom: '1px solid var(--color-light-gray, #f9f9f9)',
                cursor: 'pointer',
                background: selectedUser?.id === user.id ? 'rgba(59, 130, 246, 0.1)' : 'var(--color-surface, white)',
                borderLeft: selectedUser?.id === user.id ? '4px solid #3b82f6' : '4px solid transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ position: 'relative' }}>
                <div style={{
                  width: '45px', height: '45px', borderRadius: '50%',
                  background: 'var(--color-background, #e2e8f0)', color: 'var(--color-text-light, #64748b)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 'bold', fontSize: '0.9rem'
                }}>
                  {getInitials(user.name)}
                </div>
                {user.unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: '-2px', right: '-2px',
                    background: '#ef4444', color: 'white',
                    fontSize: '0.7rem', fontWeight: 'bold',
                    width: '20px', height: '20px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid var(--color-surface, white)'
                  }}>
                    {user.unreadCount}
                  </span>
                )}
              </div>

              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: '700', color: 'var(--color-text, #334155)' }}>{user.name}</span>
                  {user.lastMessage && (
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                      {new Date(user.lastMessage.created).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize: '0.85rem', color: user.unreadCount > 0 ? 'var(--color-text, #334155)' : 'var(--color-text-light, #94a3b8)',
                  fontWeight: user.unreadCount > 0 ? '600' : '400',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                }}>
                  {user.lastMessage ? (
                    (user.lastMessage.body || user.lastMessage.content || '').replace(/<[^>]+>/g, '').substring(0, 30) + '...'
                  ) : (
                    <i style={{ color: 'var(--color-text-light, #cbd5e1)' }}>Aucun message</i>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT: Chat Area */}
      <div className={styles.card} style={{ display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden', background: 'var(--color-background, #f8fafc)' }}>
        {selectedUser ? (
          <>
            {/* Chat Header */}
            <div style={{ padding: '15px 25px', background: 'var(--color-surface, white)', borderBottom: '1px solid var(--color-light-gray, #e2e8f0)', display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{ fontSize: '1.2rem', color: 'var(--color-text, #333)' }}>
                Discussion avec <strong>{selectedUser.name}</strong>
              </div>
              <div style={{ marginLeft: 'auto', fontSize: '0.9rem', color: '#64748b' }}>
                {selectedUser.email}
              </div>
            </div>

            {/* Messages Area */}
            <div style={{ flex: 1, padding: '25px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {chatHistory.map(msg => {
                const isMe = msg.sender === pb.authStore.model.id;
                return (
                  <div key={msg.id} style={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '70%' }}>
                    <div style={{
                      background: isMe ? 'var(--color-primary)' : 'var(--color-surface, white)',
                      color: isMe ? 'white' : 'var(--color-text, #334155)',
                      padding: '12px 16px',
                      borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      boxShadow: 'var(--shadow-sm, 0 2px 4px rgba(0,0,0,0.05))',
                      fontSize: '0.95rem',
                      lineHeight: '1.4'
                    }}>
                      {/* Render HTML content safely since we use Quill */}
                      <div dangerouslySetInnerHTML={{ __html: msg.body || msg.content || '' }} />
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px', textAlign: isMe ? 'right' : 'left' }}>
                      {new Date(msg.created).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {isMe && <FaCheckDouble style={{ marginLeft: '5px', color: msg.is_read ? '#3b82f6' : '#cbd5e1' }} />}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div style={{ padding: '20px', background: 'var(--color-surface, white)', borderTop: '1px solid var(--color-light-gray, #e2e8f0)' }}>
              <div style={{ marginBottom: '10px' }}>
                <ReactQuill
                  theme="snow"
                  value={replyContent}
                  onChange={setReplyContent}
                  placeholder="Écrivez votre réponse..."
                  style={{ height: '100px', marginBottom: '40px' }} // mb to account for toolbar
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleSendMessage}
                  className={styles.submitButton}
                  style={{
                    padding: '10px 25px',
                    borderRadius: '30px',
                    display: 'flex', alignItems: 'center', gap: '8px'
                  }}
                >
                  <FaPaperPlane /> Envoyer
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: 'var(--color-text-light, #94a3b8)' }}>
            <FaUser style={{ fontSize: '4rem', marginBottom: '20px', opacity: 0.2 }} />
            <h3 style={{ color: 'var(--color-text)' }}>Sélectionnez une discussion</h3>
            <p>Choisissez un utilisateur à gauche pour voir l'historique</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
